import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { Send, X } from "lucide-react";
import { BACKEND_BASE } from "@/lib/backend";

function resolveViewerRole() {
  const role = localStorage.getItem("dsite_role") || localStorage.getItem("dsite_admin_role");
  return role === "customer" ? "customer" : "admin";
}

function isOwnMessage(message, viewerRole) {
  if (viewerRole === "customer") return message.sender_role === "customer";
  return ["admin", "super_admin", "sub_admin"].includes(message.sender_role);
}

export default function QuoteChat({ roomId, onClose, quoteTitle, customerEmail, customerName }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const sockRef = useRef(null);
  const endRef = useRef(null);
  const viewerRole = resolveViewerRole();
  const isAdminView = viewerRole !== "customer";

  const subtitle = isAdminView
    ? `견적 「${quoteTitle || "제목 없음"}」 · 고객 ${customerName || customerEmail || "미지정"}와 1:1 상담`
    : `견적 「${quoteTitle || "제목 없음"}」 · DSiteSolution 담당자와 1:1 상담`;

  useEffect(() => {
    const token = localStorage.getItem("dsite_token");
    fetch(`${BACKEND_BASE}/api/chat-rooms/${roomId}/messages`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then(setMessages)
      .catch(() => {});

    const s = io(BACKEND_BASE, {
      path: "/api/socket.io",
      auth: { token },
      transports: ["websocket", "polling"],
    });
    sockRef.current = s;
    s.on("connect", () => s.emit("join_room", { room_id: roomId }));
    s.on("message", (m) => setMessages((prev) => [...prev, m]));
    return () => {
      s.disconnect();
    };
  }, [roomId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = () => {
    const t = input.trim();
    if (!t || !sockRef.current) return;
    sockRef.current.emit("send_message", { room_id: roomId, content: t });
    setInput("");
  };

  return (
    <div data-testid="quote-chat" className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-4">
      <div className="w-full max-w-lg h-[600px] bg-[#0a0a0a] border border-white/10 rounded-2xl flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-medium">실시간 견적 상담</div>
            <div className="text-[10px] text-white/40 mt-0.5 leading-relaxed">{subtitle}</div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white shrink-0" data-testid="quote-chat-close">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && (
            <div className="text-center text-white/30 text-xs py-8 leading-relaxed">
              {isAdminView
                ? "고객이 마이페이지에서 같은 견적의 「채팅」을 열면 이 대화방에서 실시간으로 소통할 수 있습니다."
                : "메시지를 보내면 담당자에게 실시간으로 전달됩니다."}
            </div>
          )}
          {messages.map((m) => (
            <div
              key={m.id || m.created_at}
              className={`flex ${isOwnMessage(m, viewerRole) ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm ${
                  isOwnMessage(m, viewerRole)
                    ? "bg-white text-black"
                    : "bg-white/5 text-white border border-white/10"
                }`}
              >
                <div>{m.content}</div>
                <div className={`text-[9px] mt-1 ${isOwnMessage(m, viewerRole) ? "text-black/40" : "text-white/30"}`}>
                  {m.sender_role === "customer" ? "고객" : "담당자"} · {new Date(m.created_at).toLocaleTimeString("ko-KR")}
                </div>
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="p-3 border-t border-white/10 flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                send();
              }
            }}
            placeholder="메시지 입력..."
            data-testid="quote-chat-input"
            className="flex-1 bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
          />
          <button
            onClick={send}
            data-testid="quote-chat-send"
            className="w-10 h-10 rounded-xl bg-white text-black flex items-center justify-center hover:bg-gray-200"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
